import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Gauge from '../components/Gauge';
import LiveTickerStrip from '../components/LiveTickerStrip';
import RecBanner from '../components/oracle/RecBanner';
import CompRow from '../components/CompRow';
import IncompleteState from '../components/IncompleteState';
import { computeVolumeSizing } from '../lib/oracleSizing';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';

const fmt = n => (n != null ? Math.round(n).toLocaleString() : '—');
const fmtSignalDate = d => {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][+m - 1]} ${+day}, ${y}`;
};

const SCENARIO_META = [
  { key: 'peace',      label: 'Peace',      color: '#10b981' },
  { key: 'stalemate',  label: 'Stalemate',  color: '#f59e0b' },
  { key: 'escalation', label: 'Escalation', color: '#f97316' },
  { key: 'strike',     label: 'Strike',     color: '#ef4444' },
];

export default function Oracle({ inputs = {}, results, saveManualRun, setActiveTab, aiSignal, fetchKAPSData, fetchKICPData, apiStatus, liveData }) {
  const { t, i18n } = useTranslation();
  const { isAdmin } = useAuth();
  const [showDetails, setShowDetails] = useState(false);
  const [showAiSignals, setShowAiSignals] = useState(false);
  const [bandCapture, setBandCapture] = useState(null);
  const [saving, setSaving] = useState(false);
  const [oracleNarrative, setOracleNarrative] = useState(null);

  // Fetch 30-day forecast band capture rate (admin only — others get empty via RLS)
  useEffect(() => {
    if (!isAdmin) return;
    const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    Promise.all([
      supabase.from('predictions').select('run_date, p10_7d, p90_7d').gte('run_date', cutoff),
      supabase.from('actuals').select('observed_date, dce_m1_close').gte('observed_date', cutoff),
    ]).then(([predRes, actRes]) => {
      const preds   = predRes.data || [];
      const actMap  = {};
      (actRes.data || []).forEach(a => { actMap[a.observed_date] = a.dce_m1_close; });
      const resolved = preds.filter(p => {
        const d = new Date(p.run_date + 'T00:00:00Z');
        d.setUTCDate(d.getUTCDate() + 7);
        const targetDate = d.toISOString().split('T')[0];
        return actMap[targetDate] != null;
      });
      if (resolved.length >= 3) {
        const hits = resolved.filter(p => {
          const d = new Date(p.run_date + 'T00:00:00Z');
          d.setUTCDate(d.getUTCDate() + 7);
          const actual = actMap[d.toISOString().split('T')[0]];
          return actual >= p.p10_7d && actual <= p.p90_7d;
        }).length;
        setBandCapture(Math.round(hits / resolved.length * 100));
      }
    });
  }, [isAdmin]);

  // Fetch today's AI Narrative on mount — silently no-ops if no row exists yet
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    supabase
      .from('oracle_narratives')
      .select('narrative_text, action, ev_7d, narrative_date, hormuz_tension, created_at')
      .eq('narrative_date', today)
      .single()
      .then(({ data, error }) => {
        if (!error && data) setOracleNarrative(data);
      });
  }, []);

  async function handleSaveRun() {
    if (!saveManualRun) return;
    setSaving(true);
    await saveManualRun();
    setSaving(false);
  }

  if (!results) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: 'var(--t3)', fontSize: 13 }}>
        {t('oracle.not_yet_computed')}
      </div>
    );
  }

  if (results?.status === 'incomplete') {
    return <IncompleteState onGoToDataEntry={() => setActiveTab?.('data')} />;
  }

  const { sti, msi, dpi, oti, scenarios, margins, safety, recommendation, computedAt } = results;
  const {
    pp_spot_qingdao = 10000,
    kaps_stock_mt = 0,
    kaps_transit_mt = 0,
    kaps_pr_mt = 0,
    kaps_avg_cost,
    kaps_daily_demand_mt = 63.5,
    kaps_stock_bonded_mt = 0,
    kicp_portfolio_qty_mt,
    kicp_portfolio_avg_sell,
    kicp_gloss18_qty_mt = 0,
    kicp_gloss18_sell_price,
    wti_price = 0,
    brent_price = 0,
    dce_futures_m1 = 0,
  } = inputs;

  const dpiIncomplete = kicp_portfolio_qty_mt == null || kicp_gloss18_sell_price == null;
  const otiIncomplete = kicp_portfolio_qty_mt == null || kicp_portfolio_avg_sell == null;

  // Dominant scenario
  const probs = scenarios.probabilities;
  const dominantKey = Object.keys(probs).reduce((a, b) => probs[a] > probs[b] ? a : b);
  const dominantMeta = SCENARIO_META.find(s => s.key === dominantKey);
  const dominantRange = scenarios.ppRanges[dominantKey];

  // 7-day expected vs spot
  const ev = scenarios.pp_expected_7d;
  const evDelta = Math.round(ev - pp_spot_qingdao);
  const evDeltaPct = pp_spot_qingdao > 0
    ? (Math.abs(evDelta) / pp_spot_qingdao * 100).toFixed(1)
    : '0.0';
  let forecastText, forecastColor;
  if (evDelta > 100) {
    forecastText = t('oracle.forecast_above_spot', { pct: evDeltaPct });
    forecastColor = 'var(--red)';
  } else if (evDelta < -100) {
    forecastText = t('oracle.forecast_below_spot', { pct: evDeltaPct });
    forecastColor = 'var(--grn)';
  } else {
    forecastText = t('oracle.forecast_near_spot');
    forecastColor = 'var(--t2)';
  }

  const spotVsWac = kaps_avg_cost != null ? Math.round(pp_spot_qingdao - kaps_avg_cost) : null;

  const sizing = computeVolumeSizing({
    action:            recommendation.action,
    safetyUrgency:     safety.urgency,
    ppSpot:            pp_spot_qingdao,
    scenarios,
    kapsStockMT:       kaps_stock_mt,
    kapsTransitMT:     kaps_transit_mt,
    kapsDailyDemandMT: kaps_daily_demand_mt,
  });

  // OTI normalised to 0–100 for gauge arc
  const otiNorm = Math.round((oti.total + 100) / 2);

  const safetyColor = safety.urgency === 'critical' ? 'var(--red)'
    : safety.urgency === 'urgent' ? 'var(--amb)'
    : 'var(--grn)';

  // ── Variables absorbed from Dashboard ────────────────────────────
  const lang         = i18n.language?.startsWith('zh') ? 'zh-CN' : 'en-GB';
  const lastUpd      = computedAt
    ? new Date(computedAt).toLocaleString(lang, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '—';
  const liveTag      = t('status.LIVE', { defaultValue: 'LIVE' });
  const estTag       = t('status.EST',  { defaultValue: 'EST' });
  const manTag       = t('status.MAN',  { defaultValue: 'MAN' });
  const fmtCNY       = (n) => n == null ? '—' : `¥${fmt(n)}`;
  const fmtUSD       = (n) => n == null ? '—' : `$${fmt(n)}`;
  const currRate     = inputs.currency_rate || 7.25;
  const pp           = pp_spot_qingdao;
  const sellPrice    = kicp_gloss18_sell_price;
  const portfolioSell = kicp_portfolio_avg_sell;
  const ppCost       = margins.ppCost ?? Math.round(pp * (inputs.pp_yield_ratio || 1.03));
  const conversion   = inputs.conversion_cost || 1800;
  const yieldRatio   = inputs.pp_yield_ratio || 1.03;
  const gloss18Margin    = sellPrice == null ? null : (margins.gloss18 ?? null);
  const portfolioMargin  = portfolioSell == null ? null : (margins.portfolio ?? null);
  const breakevenSell    = margins.gloss18BreakevenSellPrice ?? (ppCost != null ? Math.round(ppCost + conversion) : null);
  const breakevenPP      = margins.gloss18Breakeven ?? (sellPrice != null ? Math.round((sellPrice - conversion) / yieldRatio) : null);
  const breakevenPctNeed = (breakevenSell != null && sellPrice != null) ? Math.round(((breakevenSell / sellPrice) - 1) * 100) : null;
  const totalAvail   = safety.totalAvailable ?? null;
  const transit      = kaps_transit_mt;
  const coverDays    = safety.coverageDays ?? null;
  const stockAvgCost = kaps_avg_cost ?? null;
  const stockSpotAdv = stockAvgCost != null ? pp - stockAvgCost : null;
  const gapDays      = safety.gapDays ?? null;
  const producers = [
    { name: t('dashboard.producer_zhenhai'),  grade: 'T30S',     price: inputs.pp_spot_zhenhai  || 10100, status: t('dashboard.producer_status_normal'), statusType: 'neutral' },
    { name: t('dashboard.producer_jiujiang'), grade: 'T30S',     price: inputs.pp_spot_jiujiang || 9200,  status: t('dashboard.producer_status_normal'), statusType: 'neutral' },
    { name: t('dashboard.producer_daqing'),   grade: 'T30S',     price: inputs.pp_spot_daqing   || 9070,  status: t('dashboard.producer_status_normal'), statusType: 'neutral' },
    { name: t('dashboard.producer_fushun'),   grade: 'L5E89',    price: inputs.pp_spot_fushun   || 9050,  status: t('dashboard.producer_status_normal'), statusType: 'neutral' },
    { name: `🎯 ${t('dashboard.producer_qingdao')}`, grade: 'PPH-F03Q', price: pp, status: t('dashboard.producer_status_yours'), statusType: 'blu', isYours: true },
  ];

  return (
    <>
      {/* A — Header */}
      <div className="page-hdr" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>🔮</div>
        <h2 style={{ letterSpacing: '.06em' }}>{t('oracle.title')}</h2>
        <p style={{ fontSize: 13, color: 'var(--t2)', marginTop: 4 }}>{t('oracle.subtitle')}</p>
        <p style={{ fontSize: 12, color: 'var(--t2)', marginTop: 3 }}>
          {t('oracle.channel_scope')} · {computedAt
            ? new Date(computedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
            : t('oracle.not_yet_computed')}
        </p>
        {isAdmin && (
          <p style={{ fontSize: 12, color: 'var(--t2)', marginTop: 2 }}>
            {bandCapture != null
              ? t('veritas.band_capture_oracle', { pct: bandCapture })
              : t('veritas.band_capture_na')}
          </p>
        )}
      </div>

      {/* AI Narrative Card — renders only when today's narrative exists */}
      {oracleNarrative && (() => {
        const tension = oracleNarrative.hormuz_tension;
        const tensionColor = tension <= 2 ? 'var(--green)' : tension === 3 ? 'var(--amber)' : 'var(--red)';
        const createdTime = oracleNarrative.created_at
          ? new Date(oracleNarrative.created_at).toISOString().slice(11, 16) + ' UTC'
          : '';
        return (
          <div style={{
            background: 'var(--bg2)',
            border: '1px solid var(--bg5)',
            borderTop: '3px solid var(--violet)',
            borderRadius: 'var(--radius)',
            padding: 'var(--space-4) var(--space-5)',
            marginBottom: 'var(--space-4)',
          }}>
            {/* Header row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div>
                <div className="label-mono">🧠 AI PROCUREMENT BRIEF</div>
                <div style={{ fontSize: 10, color: 'var(--t3)', marginTop: 1 }}>AI 采购简报</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--t3)', marginTop: 2 }}>
                  Generated by Claude · {createdTime}
                </div>
              </div>
              {tension != null && (
                <div className="label-mono" style={{ color: tensionColor, textAlign: 'right', flexShrink: 0 }}>
                  🛢️ HORMUZ {tension}/5
                </div>
              )}
            </div>

            {/* Narrative body */}
            <p style={{ fontSize: 14, color: 'var(--t1)', lineHeight: 1.6, margin: 0 }}>
              {oracleNarrative.narrative_text}
            </p>

            {/* Footer */}
            {oracleNarrative.ev_7d != null && (
              <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--t3)', marginTop: 10 }}>
                7d expected ¥{fmt(oracleNarrative.ev_7d)} · Model says {oracleNarrative.action}
              </div>
            )}
          </div>
        );
      })()}

      {/* B — Recommendation banner */}
      <RecBanner recommendation={recommendation} sizing={sizing} />

      {/* Save Run button (VERITAS manual prediction capture) */}
      {saveManualRun && (
        <button
          className="btn"
          onClick={handleSaveRun}
          disabled={saving || results?.status === 'incomplete'}
          title={results?.status === 'incomplete' ? t('common.save_run_blocked_tooltip') : undefined}
          style={{ width: '100%', fontSize: 12, marginBottom: 12 }}
        >
          {saving ? '…' : t('veritas.save_run_btn')}
        </button>
      )}

      {/* Live Ticker Strip */}
      <LiveTickerStrip inputs={inputs} liveData={liveData} />

      {/* C — P50 / P10 / P90 strip */}
      <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '16px 20px' }}>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontSize: 12, color: 'var(--t2)', letterSpacing: '.06em', marginBottom: 4 }}>P10  BULL</div>
          <div className="oracle-p1090-value" style={{ color: 'var(--grn)' }}>¥{fmt(scenarios.pp_p10_7d)}</div>
        </div>
        <div style={{
          textAlign: 'center', flex: 1.3,
          borderLeft: '1px solid var(--border)',
          borderRight: '1px solid var(--border)',
          padding: '0 20px',
        }}>
          <div style={{ fontSize: 12, color: 'var(--t2)', letterSpacing: '.06em', marginBottom: 4 }}>7-DAY  EXPECTED  (P50)</div>
          <div className="oracle-p50-value">¥{fmt(ev)}</div>
          <div style={{ fontSize: 12, color: forecastColor, marginTop: 2 }}>{forecastText}</div>
        </div>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontSize: 12, color: 'var(--t2)', letterSpacing: '.06em', marginBottom: 4 }}>P90  BEAR</div>
          <div className="oracle-p1090-value" style={{ color: 'var(--red)' }}>¥{fmt(scenarios.pp_p90_7d)}</div>
        </div>
      </div>

      {/* C2 — AI Market Signals */}
      <div className="card" style={{ padding: '14px 16px' }}>
        {!aiSignal ? (
          <div style={{ fontSize: 12, color: 'var(--t2)', textAlign: 'center', lineHeight: 1.6 }}>
            AI signals not yet available — first run scheduled daily at 06:00 UTC<br />
            <span>AI 信号暂不可用——每日 UTC 06:00 自动运行</span>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <span style={{ fontSize: 12, fontWeight: 600 }}>🤖 AI Market Signals</span>
                <span style={{ fontSize: 12, color: 'var(--t2)', marginLeft: 4 }}>/ AI 市场信号</span>
                <span style={{ fontSize: 12, color: 'var(--t2)', marginLeft: 8 }}>{fmtSignalDate(aiSignal.signal_date)}</span>
              </div>
              <button
                onClick={() => setShowAiSignals(s => !s)}
                className="btn"
                style={{ fontSize: 12, padding: '2px 10px', minHeight: 'unset' }}
              >
                {showAiSignals ? '▲' : '▼'}
              </button>
            </div>
            {showAiSignals && (
              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                  {[
                    { label: 'Hormuz', value: aiSignal.hormuz_tension,
                      lbl: { 1:'Calm', 2:'Minor', 3:'Significant', 4:'Crisis', 5:'Closed' }[aiSignal.hormuz_tension],
                      col: { 1:'var(--grn)', 2:'var(--grn)', 3:'var(--amb)', 4:'var(--red)', 5:'var(--red)' }[aiSignal.hormuz_tension] },
                    { label: 'OPEC Supply', value: aiSignal.opec_supply, signed: true,
                      lbl: ({ '-3':'Major Cut', '-2':'Cut', '-1':'Mild Cut', '0':'Flat', '1':'Mild Rise', '2':'Rise', '3':'Major Rise' })[String(aiSignal.opec_supply)] },
                    { label: 'China Stim.', value: aiSignal.china_stimulus, signed: true,
                      lbl: ({ '-3':'Contraction', '-2':'Weak', '-1':'Soft', '0':'Neutral', '1':'Mild', '2':'Moderate', '3':'Strong' })[String(aiSignal.china_stimulus)] },
                    { label: 'Petrochem', value: aiSignal.petrochem_sentiment, signed: true,
                      lbl: ({ '-3':'Very Weak', '-2':'Weak', '-1':'Soft', '0':'Neutral', '1':'Firm', '2':'Strong', '3':'Very Strong' })[String(aiSignal.petrochem_sentiment)] },
                  ].map(({ label, value, signed, lbl, col }) => {
                    const c = col || (Math.abs(value) >= 3 ? 'var(--red)' : Math.abs(value) >= 2 ? 'var(--amb)' : Math.abs(value) >= 1 ? 'var(--t2)' : 'var(--t3)');
                    return (
                      <div key={label} style={{ flex: 1, minWidth: 68, background: 'var(--bg4)', borderRadius: 4, padding: '6px 8px' }}>
                        <div style={{ fontSize: 12, color: 'var(--t2)', marginBottom: 2 }}>{label.toUpperCase()}</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: c }}>{signed && value > 0 ? `+${value}` : value}</div>
                        <div style={{ fontSize: 12, color: c }}>{lbl || '—'}</div>
                      </div>
                    );
                  })}
                </div>
                {aiSignal.key_events && (
                  <div style={{ background: 'var(--bg4)', borderRadius: 4, padding: '8px 10px', fontSize: 12, color: 'var(--t2)', marginBottom: 10, lineHeight: 1.5 }}>
                    {aiSignal.key_events}
                  </div>
                )}
                <div style={{ fontSize: 12, color: 'var(--t2)', borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                  {(() => {
                    const parts = [
                      sti.aiAdjusted ? `STI adjusted ${sti.aiDelta > 0 ? '+' : ''}${sti.aiDelta}` : null,
                      msi.aiAdjusted ? `MSI adjusted ${msi.aiDelta > 0 ? '+' : ''}${msi.aiDelta}` : null,
                      dpi.aiAdjusted ? `DPI adjusted ${dpi.aiDelta > 0 ? '+' : ''}${dpi.aiDelta}` : null,
                    ].filter(Boolean);
                    return parts.length > 0
                      ? parts.join(' · ')
                      : 'No index adjustment from today\'s signals / 今日信号无指数调整';
                  })()}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* D — 4 compact index gauges */}
      <div className="oracle-gauge-grid">
        {[
          { id: 'sti', label: 'STI', score: sti.total,  color: sti.color, readout: String(sti.total),  sub: sti.label, incomplete: false, aiAdjusted: sti.aiAdjusted },
          { id: 'msi', label: 'MSI', score: msi.total,  color: msi.color, readout: String(msi.total),  sub: msi.label, incomplete: false, aiAdjusted: msi.aiAdjusted },
          { id: 'dpi', label: 'DPI', score: dpi.total,  color: dpi.color, readout: String(dpi.total),  sub: dpi.label, incomplete: dpiIncomplete, tooltip: t('oracle.dpi_incomplete_tooltip'), aiAdjusted: dpi.aiAdjusted },
          { id: 'oti', label: 'OTI', score: otiNorm,    color: oti.color, readout: `${oti.total > 0 ? '+' : ''}${oti.total}`, sub: oti.label, incomplete: otiIncomplete, tooltip: t('oracle.oti_incomplete_tooltip') },
        ].map(({ id, label, score, color, readout, sub, incomplete, tooltip, aiAdjusted }) => (
          <div key={id} className="card" style={{ textAlign: 'center', padding: '14px 8px' }} title={incomplete ? tooltip : undefined}>
            <div style={{ fontSize: 12, color: 'var(--t2)', letterSpacing: '.08em', marginBottom: 4 }}>
              {label}{incomplete && <span style={{ color: 'var(--amb)', marginLeft: 2 }}>*</span>}{aiAdjusted && <span style={{ color: 'var(--amb)', marginLeft: 2 }} title="AI-adjusted">*</span>}
            </div>
            <div className="oracle-gauge-cell" style={{ display: 'flex', justifyContent: 'center' }}>
              <Gauge score={score} color={color} compact />
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>{readout}</div>
            <div style={{ fontSize: 12, color: 'var(--t2)' }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* STI / MSI Component Breakdown */}
      <div className="sti-msi-grid">
        <div className="card">
          <div className="card-title"><span className="card-title-text">{t('dashboard.components_sti')}</span></div>
          <CompRow label={t('dashboard.comp_a')} score={sti.components.a2} color="amb" weight="22%" srcTag="EST"  srcTagLabel={estTag} />
          <CompRow label={t('dashboard.comp_b')} score={sti.components.b2} color={sti.components.b2 > 70 ? 'red' : 'amb'} weight="28%" srcTag="LIVE" srcTagLabel={liveTag} />
          <CompRow label={t('dashboard.comp_c')} score={sti.components.c2} color={sti.components.c2 > 70 ? 'red' : 'amb'} weight="18%" srcTag="LIVE" srcTagLabel={liveTag} />
          <CompRow label={t('dashboard.comp_d')} score={sti.components.d2} color="amb" weight="20%" srcTag="EST"  srcTagLabel={estTag} />
          <CompRow label={t('dashboard.comp_e')} score={sti.components.e2} color="amb" weight="12%" srcTag="EST"  srcTagLabel={estTag} />
        </div>
        <div className="card">
          <div className="card-title"><span className="card-title-text">{t('dashboard.components_msi')}</span></div>
          <CompRow label={t('dashboard.comp_f')} score={msi.components.f2} color={msi.components.f2 > 60 ? 'amb' : 'grn'} weight="30%" srcTag="MAN" srcTagLabel={manTag} />
          <CompRow label={t('dashboard.comp_g')} score={msi.components.g2} color={msi.components.g2 > 60 ? 'amb' : 'grn'} weight="28%" srcTag="MAN" srcTagLabel={manTag} />
          <CompRow label={t('dashboard.comp_h')} score={msi.components.h2} color="amb" weight="22%" srcTag="MAN" srcTagLabel={manTag} />
          <CompRow label={t('dashboard.comp_i')} score={msi.components.i2} color={msi.components.i2 > 60 ? 'amb' : 'grn'} weight="20%" srcTag="MAN" srcTagLabel={manTag} />
        </div>
      </div>

      {/* E — Bayesian scenarios */}
      <div className="card">
        <div className="card-hdr" style={{ marginBottom: 12 }}>Bayesian Scenarios</div>
        {dominantMeta && (
          <div style={{
            marginBottom: 14,
            padding: '8px 12px',
            background: `${dominantMeta.color}18`,
            border: `1px solid ${dominantMeta.color}40`,
            borderRadius: 6,
            fontSize: 12,
            color: 'var(--t1)',
          }}>
            {t('oracle.dominant_scenario', {
              scenario: dominantMeta.label,
              pct: probs[dominantKey],
              low: fmt(dominantRange?.low),
              high: fmt(dominantRange?.high),
            })}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {SCENARIO_META.map(({ key, label, color }) => {
            const pct = probs[key] || 0;
            const range = scenarios.ppRanges[key];
            return (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, color: 'var(--t2)', width: 76, flex: 'none' }}>{label}</span>
                <div style={{ flex: 1, height: 8, background: 'var(--bg4)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width .65s ease' }} />
                </div>
                <span style={{ fontSize: 12, color, width: 34, textAlign: 'right', flex: 'none', fontWeight: 600 }}>{pct}%</span>
                <span style={{ fontSize: 12, color: 'var(--t2)', width: 126, flex: 'none', textAlign: 'right' }}>
                  ¥{fmt(range?.low)}–¥{fmt(range?.high)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* F — Safety / Margin / WAC cards */}
      <div className="oracle-kpi-grid">
        <div className="card">
          <div style={{ fontSize: 12, color: 'var(--t2)', letterSpacing: '.06em', marginBottom: 6 }}>SAFETY COVERAGE</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: safetyColor }}>{safety.coverageDays}d</div>
          <div style={{ fontSize: 12, color: 'var(--t2)', marginTop: 4 }}>
            {safety.urgency === 'critical'
              ? t('oracle.safety_breach_days', { days: safety.daysToSafetyBreach })
              : t('oracle.coverage_incl_transit')}
          </div>
          {safety.isBreachImminent && (
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--amb)' }}>
              ⚠ {safety.gapDays > 0 ? `${safety.gapDays}d gap vs lead time` : 'Within lead time window'}
            </div>
          )}
        </div>

        <div className="card">
          <div style={{ fontSize: 12, color: 'var(--t2)', letterSpacing: '.06em', marginBottom: 6 }}>{t('oracle.gloss18_margin_label').toUpperCase()}</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: kicp_gloss18_sell_price == null ? 'var(--t3)' : margins.gloss18 >= 0 ? 'var(--grn)' : 'var(--red)' }}>
            {kicp_gloss18_sell_price == null ? '—' : `${margins.gloss18 >= 0 ? '+' : ''}¥${fmt(margins.gloss18)}`}
          </div>
          <div style={{ fontSize: 12, color: 'var(--t2)', marginTop: 4 }}>
            {kicp_gloss18_sell_price == null ? '' : `${t('oracle.breakeven_pp')}: ¥${fmt(margins.gloss18Breakeven)}`}
          </div>
        </div>

        <div className="card">
          <div style={{ fontSize: 12, color: 'var(--t2)', letterSpacing: '.06em', marginBottom: 6 }}>{t('oracle.spot_vs_wac_label').toUpperCase()}</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: spotVsWac == null ? 'var(--t3)' : spotVsWac <= 0 ? 'var(--grn)' : 'var(--red)' }}>
            {spotVsWac == null ? '—' : `${spotVsWac >= 0 ? '+' : ''}¥${fmt(spotVsWac)}`}
          </div>
          <div style={{ fontSize: 12, color: 'var(--t2)', marginTop: 4 }}>
            {spotVsWac != null ? t('oracle.spot_vs_wac_detail', { spot: fmt(pp_spot_qingdao), wac: fmt(kaps_avg_cost) }) : ''}
          </div>
        </div>
      </div>

      {/* Detailed Margin + Portfolio cards */}
      <div className="g2 mb-16">
        {/* Gloss 18mm */}
        <div className="card">
          <div className="card-title"><span className="card-title-text">{t('dashboard.gloss18_card_title')}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontSize: 13, color: 'var(--t1)' }}>{t('dashboard.margin_sell_price')}</span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 13, color: sellPrice != null ? 'var(--green)' : 'var(--t3)', fontWeight: 700 }}>{sellPrice != null ? `+ ${fmtCNY(sellPrice)}` : '—'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontSize: 13, color: 'var(--t1)' }}>{t('dashboard.margin_pp_cost_formula', { pp: fmtCNY(pp), yield: yieldRatio })}</span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--red)', fontWeight: 700 }}>−&nbsp;{fmtCNY(ppCost)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontSize: 13, color: 'var(--t1)' }}>{t('dashboard.margin_conversion_cost')}</span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--red)', fontWeight: 700 }}>−&nbsp;{fmtCNY(conversion)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 6px' }}>
            <span style={{ fontSize: 13, color: 'var(--t0)', fontWeight: 600 }}>{t('dashboard.margin_net_margin')}</span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 18, fontWeight: 800, color: gloss18Margin == null ? 'var(--t3)' : gloss18Margin >= 0 ? 'var(--green)' : 'var(--red)', letterSpacing: '-.01em' }}>
              {gloss18Margin == null ? '—' : `${gloss18Margin >= 0 ? '+' : ''}${fmtCNY(gloss18Margin)}/MT`}
            </span>
          </div>
          {gloss18Margin != null && gloss18Margin < 0 && (
            <div style={{ marginTop: 12, padding: '10px 12px', background: 'rgba(245,158,11,.08)', border: '1px solid var(--amber-b)', borderRadius: 8, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 14, flexShrink: 0 }}>⚠️</span>
              <div style={{ fontSize: 12, color: 'var(--t1)', lineHeight: 1.5 }} dangerouslySetInnerHTML={{
                __html: t('dashboard.margin_breakeven_alert', {
                  sell: `<strong style="color:var(--amber)">${fmtCNY(breakevenSell)}/MT</strong>`,
                  pct: breakevenPctNeed,
                  pp: `<strong style="color:var(--amber)">${fmtCNY(breakevenPP)}/MT</strong>`,
                })
              }} />
            </div>
          )}
        </div>

        {/* Portfolio margin */}
        <div className="card">
          <div className="card-title"><span className="card-title-text">{t('dashboard.portfolio_card_title')}</span></div>
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div className="label-mono mb-4" style={{ textAlign: 'center' }}>{t('dashboard.portfolio_weighted_avg')}</div>
            <div style={{ fontSize: 36, fontWeight: 800, color: portfolioMargin == null ? 'var(--t3)' : portfolioMargin >= 0 ? 'var(--green)' : 'var(--red)', lineHeight: 1, letterSpacing: '-.04em', marginBottom: 4 }}>
              {portfolioMargin == null ? '—' : <>{fmtCNY(portfolioMargin)}<span style={{ fontSize: 16, color: 'var(--t2)', fontWeight: 500 }}>/MT</span></>}
            </div>
            <div style={{ fontSize: 12, color: 'var(--t2)', fontFamily: 'var(--mono)' }}>
              {t('dashboard.portfolio_breakdown', { sell: fmtCNY(portfolioSell), pp: fmtCNY(ppCost), conv: fmtCNY(conversion) })}
            </div>
          </div>
          {/* 4 mini-cells */}
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div className="label-mono mb-4">{t('dashboard.available_stock')}</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--t0)', letterSpacing: '-.02em' }}>{fmt(totalAvail)} {t('dashboard.mt')}</div>
              <div style={{ fontSize: 10, color: 'var(--t2)', marginTop: 2 }}>{transit != null ? t('dashboard.plus_transit', { n: fmt(transit) }) : ''}</div>
            </div>
            <div>
              <div className="label-mono mb-4">{t('dashboard.coverage')}</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--t0)', letterSpacing: '-.02em' }}>{coverDays} {t('dashboard.days_unit')}</div>
              <div style={{ fontSize: 10, color: 'var(--t2)', marginTop: 2 }}>{t('dashboard.incl_transit')}</div>
            </div>
            <div>
              <div className="label-mono mb-4">{t('dashboard.stock_avg_cost')}</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--t0)', letterSpacing: '-.02em' }}>{fmtCNY(stockAvgCost)}</div>
              <div style={{ fontSize: 10, color: 'var(--t2)', marginTop: 2 }}>{stockSpotAdv != null ? t('dashboard.vs_spot_adv', { n: fmtCNY(stockSpotAdv) }) : ''}</div>
            </div>
            <div>
              <div className="label-mono mb-4">{t('dashboard.safety_breach')}</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: safety.daysToSafetyBreach != null && safety.daysToSafetyBreach < 14 ? 'var(--red)' : 'var(--t0)', letterSpacing: '-.02em' }}>{fmt(safety.daysToSafetyBreach)} {safety.daysToSafetyBreach != null ? t('dashboard.days_unit') : ''}</div>
              <div style={{ fontSize: 10, color: 'var(--t2)', marginTop: 2 }}>{t('dashboard.gap_vs_lead_time', { n: fmt(gapDays) })}</div>
            </div>
          </div>
        </div>
      </div>

      {/* China PP Producers Snapshot */}
      <div className="card mb-16">
        <div className="card-title"><span className="card-title-text">{t('dashboard.snapshot_card_title')}</span></div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ minWidth: 580 }}>
            <thead>
              <tr>
                <th>{t('dashboard.th_producer')}</th>
                <th>{t('dashboard.th_grade')}</th>
                <th style={{ textAlign: 'right' }}>{t('dashboard.th_price_yuan')}</th>
                <th style={{ textAlign: 'right' }}>{t('dashboard.th_usd_equiv')}</th>
                <th style={{ textAlign: 'right' }}>{t('dashboard.th_vs_your_cost')}</th>
                <th>{t('dashboard.th_status')}</th>
              </tr>
            </thead>
            <tbody>
              {producers.map((p, i) => {
                const usd = (p.price / currRate).toFixed(0);
                const vsCost = (stockAvgCost != null && stockAvgCost !== 0) ? p.price - stockAvgCost : null;
                return (
                  <tr key={i}>
                    <td style={{ fontWeight: p.isYours ? 700 : 500, color: p.isYours ? 'var(--blue)' : 'var(--t0)' }}>{p.name}</td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--t1)' }}>{p.grade}</td>
                    <td style={{ fontFamily: 'var(--mono)', textAlign: 'right', fontWeight: 700, color: 'var(--t0)' }}>{fmtCNY(p.price)}</td>
                    <td style={{ fontFamily: 'var(--mono)', textAlign: 'right', color: 'var(--t1)' }}>{fmtUSD(usd)}</td>
                    <td style={{ fontFamily: 'var(--mono)', textAlign: 'right', color: vsCost == null ? 'var(--t3)' : vsCost >= 0 ? 'var(--red)' : 'var(--green)', fontWeight: 600 }}>
                      {vsCost == null ? '—' : `${vsCost >= 0 ? '+' : '−'}${fmtCNY(Math.abs(vsCost))}`}
                    </td>
                    <td><span className={`badge badge-${p.statusType}`}>{p.status}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* KAPS / KICP Refresh */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button
          onClick={fetchKAPSData}
          className="btn btn-sm"
          style={{ borderColor: apiStatus?.kaps === 'ok' ? 'var(--green-b)' : undefined, color: apiStatus?.kaps === 'ok' ? 'var(--green)' : undefined }}
        >
          🏭 {apiStatus?.kaps === 'loading' ? t('dashboard.btn_kaps_fetching') : apiStatus?.kaps === 'ok' ? t('dashboard.btn_kaps_live') : apiStatus?.kaps === 'error' ? t('dashboard.btn_kaps_error') : t('dashboard.btn_kaps_fetch')}
        </button>
        <button
          onClick={fetchKICPData}
          className="btn btn-sm"
          style={{ borderColor: apiStatus?.kicp === 'ok' ? 'var(--green-b)' : undefined, color: apiStatus?.kicp === 'ok' ? 'var(--green)' : undefined }}
        >
          📦 {apiStatus?.kicp === 'loading' ? t('dashboard.btn_kaps_fetching') : apiStatus?.kicp === 'ok' ? t('dashboard.btn_kicp_live') : apiStatus?.kicp === 'error' ? t('dashboard.btn_kicp_error') : t('dashboard.btn_kicp_fetch')}
        </button>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--t3)', display: 'flex', alignItems: 'center', gap: 4 }}>
          {t('dashboard.last_updated', { time: lastUpd })}
        </div>
      </div>

      {/* G — Collapsible details */}
      <button
        onClick={() => setShowDetails(d => !d)}
        className="btn"
        style={{ width: '100%', textAlign: 'center', fontSize: 12, minHeight: 44 }}
      >
        {showDetails ? t('oracle.hide_details') : t('oracle.show_details')}
      </button>

      {showDetails && (
        <>
          {/* DPI + OTI component breakdown */}
          <div className="oracle-comp-grid">
            <div className="card">
              <div className="card-hdr" style={{ marginBottom: 10 }}>DPI Components</div>
              {dpi.components && (
                <>
                  <CompRow label={t('oracle.dpi_comp_util')}   score={dpi.components.utilScore}   color={dpi.color} />
                  <CompRow label={t('oracle.dpi_comp_order')}  score={dpi.components.orderScore}  color={dpi.color} />
                  <CompRow label={t('oracle.dpi_comp_margin')} score={dpi.components.marginScore} color={dpi.color} />
                </>
              )}
            </div>
            <div className="card">
              <div className="card-hdr" style={{ marginBottom: 10 }}>OTI Components</div>
              {oti.components && (
                <>
                  <CompRow label={t('oracle.oti_comp_vol')}   score={Math.max(0, oti.components.volMom)}   color={oti.color} />
                  <CompRow label={t('oracle.oti_comp_price')} score={Math.max(0, oti.components.priceMom)} color={oti.color} />
                  <CompRow label={t('oracle.oti_comp_rev')}   score={Math.max(0, oti.components.revScore)}  color={oti.color} />
                </>
              )}
            </div>
          </div>

          {/* KAPS inventory snapshot */}
          <div className="card">
            <div className="card-hdr" style={{ marginBottom: 10 }}>KAPS Inventory Snapshot</div>
            <div className="oracle-snapshot-grid">
              {[
                { label: 'Stock',               value: `${fmt(kaps_stock_mt)} MT` },
                { label: 'Bonded',              value: `${fmt(kaps_stock_bonded_mt)} MT` },
                { label: 'In Transit',          value: `${fmt(kaps_transit_mt)} MT` },
                { label: 'On PR',               value: `${fmt(kaps_pr_mt)} MT` },
                { label: 'Total Available',     value: `${fmt(safety.totalAvailable)} MT` },
                { label: 'Coverage (w/ transit)', value: `${safety.coverageDays}d` },
                { label: 'WAC',                 value: `¥${fmt(kaps_avg_cost)}` },
                { label: 'Daily Demand',        value: `${kaps_daily_demand_mt} MT/d` },
              ].map(({ label, value }) => (
                <div key={label} style={{ paddingTop: 4 }}>
                  <div style={{ fontSize: 12, color: 'var(--t2)', marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Market snapshot */}
          <div className="card">
            <div className="card-hdr" style={{ marginBottom: 10 }}>Market Snapshot</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed' }}>
              <tbody>
                {[
                  { label: 'WTI Crude',            value: `$${fmt(wti_price)}/bbl` },
                  { label: 'Brent Crude',           value: `$${fmt(brent_price)}/bbl` },
                  { label: 'PP Spot (Qingdao)',     value: `¥${fmt(pp_spot_qingdao)}/MT` },
                  { label: 'DCE PP M1 Futures',    value: `¥${fmt(dce_futures_m1)}/MT` },
                  { label: 'Gloss 18µm Sell Price', value: `¥${fmt(kicp_gloss18_sell_price)}/MT` },
                  { label: 'Portfolio Avg Sell',    value: `¥${fmt(kicp_portfolio_avg_sell)}/MT` },
                ].map(({ label, value }) => (
                  <tr key={label} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 0', color: 'var(--t2)', width: '58%' }}>{label}</td>
                    <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* KICP portfolio */}
          <div className="card">
            <div className="card-hdr" style={{ marginBottom: 10 }}>KICP Portfolio</div>
            <div className="oracle-snapshot-grid">
              {[
                { label: 'Portfolio Qty',         value: `${fmt(kicp_portfolio_qty_mt)} MT` },
                { label: 'Portfolio Avg Sell',    value: `¥${fmt(kicp_portfolio_avg_sell)}` },
                { label: 'Gloss 18µm Qty',        value: `${fmt(kicp_gloss18_qty_mt)} MT` },
                { label: 'Gloss 18µm Sell',       value: `¥${fmt(kicp_gloss18_sell_price)}` },
                { label: 'Portfolio Margin',      value: `¥${fmt(margins.portfolio)}/MT`,      color: margins.portfolio >= 0 ? 'var(--grn)' : 'var(--red)' },
                { label: 'Gloss 18µm Margin',     value: `¥${fmt(margins.gloss18)}/MT`,        color: margins.gloss18 >= 0 ? 'var(--grn)' : 'var(--red)' },
                { label: 'Breakeven PP',          value: `¥${fmt(margins.gloss18Breakeven)}/MT` },
                { label: 'PP Cost (incl. yield)', value: `¥${fmt(margins.ppCost)}/MT` },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ paddingTop: 4 }}>
                  <div style={{ fontSize: 12, color: 'var(--t2)', marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: color || 'inherit' }}>{value}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{
            textAlign: 'center',
            fontSize: 12,
            color: 'var(--t2)',
            fontFamily: 'var(--mono)',
            marginTop: 16,
            opacity: 0.7,
          }}>
            {t('oracle.scope_footnote')}
          </div>
        </>
      )}
      {/* Mobile typography improvements */}
      <style>{`
        @media (max-width: 640px) {
          .page-container { padding: 14px 12px 80px !important; }
          .page-hdr h2 { font-size: 19px !important; }
          .page-hdr p { font-size: 13px !important; line-height: 1.6 !important; }
          .card { padding: 14px 14px !important; }
          .card-title { margin-bottom: 10px !important; }
          .card-title-text { font-size: 10px !important; letter-spacing: .12em !important; }
          .scenario-range { display: none !important; }
          .scenario-bar { width: 56px !important; }
          .data-table th, .data-table td { padding: 7px 9px !important; font-size: 12px !important; }
          .label-mono { font-size: 10px !important; }
          .g4 { gap: 10px !important; }
          .g3 { gap: 12px !important; }
          .g2 { gap: 12px !important; }
        }
        @media (max-width: 420px) {
          .page-hdr h2 { font-size: 17px !important; }
          .data-table th, .data-table td { font-size: 11px !important; padding: 6px 7px !important; }
        }
      `}</style>
    </>
  );
}
